/**
 * عقد طبقة الوصول إلى البيانات.
 *
 * الخدمات تعتمد على هذا العقد فقط، ولا تعرف إن كانت البيانات في IndexedDB
 * أو في Supabase. هذا ما يسمح بـ:
 *   - العمل دون اتصال على نفس منطق الأعمال،
 *   - تبديل المخزن دون إعادة كتابة الخدمات،
 *   - اختبار القواعد على تنفيذ حقيقي لا على وهم (mocks).
 *
 * لا يكفي هذا العقد للحماية: في وضع Supabase تبقى RLS في PostgreSQL هي
 * المرجع النهائي، وفي الوضع المحلي يطبّق محرك السياسات نفس القواعد.
 */

import type {
  AppNotification,
  AssignmentMode,
  Attachment,
  AttachmentDeletion,
  AuditLog,
  Brand,
  BrandBranchLink,
  Branch,
  Company,
  CompanyModule,
  CompletionRule,
  Membership,
  MembershipProfile,
  MembershipAccessGrant,
  MembershipPlacement,
  MembershipRole,
  PlatformOwner,
  PlatformMember,
  PlatformMemberPermission,
  Profile,
  Role,
  RolePermission,
  RoleScope,
  SettingRecord,
  SupervisionRelation,
  Task,
  TaskAssignee,
  TaskCategory,
  TaskPriority,
  TaskStatusHistory,
  TaskStep,
  TaskTemplate,
  TaskTemplateAssignee,
  TaskType,
  TaskVisibility,
  ThemeTokens,
} from '@/domain/types';

/** نصوص صفحة الدخول العامة (تظهر قبل تسجيل الدخول). */
export interface PlatformBranding {
  productName: string;
  eyebrow: string;
  headline: string;
  body: string;
  footer: string;
}

export const DEFAULT_PLATFORM_BRANDING: PlatformBranding = {
  productName: 'TaskFlow',
  eyebrow: 'تعدد المنشآت • صلاحيات دقيقة • متابعة ميدانية',
  headline: 'كل مهمة موثّقة، وكل صلاحية محدودة، وكل منشأة معزولة.',
  body:
    'منصة تشغيل للمهام اليومية والمتكررة، مع إثباتات إنجاز، واعتماد، وتقارير مبنية على نفس صلاحيات البيانات.',
  footer: 'لا تُرسل كلمات المرور أو مفاتيح الخدمة في أي سجل أو دعم.',
};
import type { UUID, TimeZone } from '@/domain/ids';
import type { PermissionKey } from '@/domain/permissions';
import type {
  BillingInterval,
  CompanyInvite,
  CompanySubscription,
  PaymentStatus,
  PlatformOperationsSettings,
  RegistrationRequest,
  SubscriptionEvent,
  SubscriptionPayment,
  SubscriptionPlan,
  SubscriptionSource,
  SubscriptionStatus,
} from '@/domain/billing';

/** كل الجداول التي تتعامل معها الخدمات. */
export interface Tables {
  profiles: Profile;
  membership_profiles: MembershipProfile;
  platform_owners: PlatformOwner;
  platform_members: PlatformMember;
  platform_member_permissions: PlatformMemberPermission;
  platform_settings: PlatformOperationsSettings;
  subscription_plans: SubscriptionPlan;
  company_subscriptions: CompanySubscription;
  subscription_events: SubscriptionEvent;
  subscription_payments: SubscriptionPayment;
  company_invites: CompanyInvite;
  registration_requests: RegistrationRequest;
  companies: Company;
  brands: Brand;
  branches: Branch;
  brand_branch_links: BrandBranchLink;
  task_categories: TaskCategory;
  memberships: Membership;
  membership_placements: MembershipPlacement;
  membership_roles: MembershipRole;
  membership_access_grants: MembershipAccessGrant;
  roles: Role;
  role_permissions: RolePermission;
  role_scopes: RoleScope;
  supervision_relations: SupervisionRelation;
  task_templates: TaskTemplate;
  task_template_assignees: TaskTemplateAssignee;
  tasks: Task;
  task_assignees: TaskAssignee;
  task_steps: TaskStep;
  task_status_history: TaskStatusHistory;
  attachments: Attachment;
  attachment_deletions: AttachmentDeletion;
  notifications: AppNotification;
  settings: SettingRecord;
  company_modules: CompanyModule;
  audit_logs: AuditLog;
}

export type TableName = keyof Tables;
export type Row<T extends TableName> = Tables[T];

export type FilterOperator =
  | { op: 'eq'; value: unknown }
  | { op: 'neq'; value: unknown }
  | { op: 'in'; value: unknown[] }
  | { op: 'isNull' }
  | { op: 'isNotNull' }
  | { op: 'gt'; value: unknown }
  | { op: 'gte'; value: unknown }
  | { op: 'lt'; value: unknown }
  | { op: 'lte'; value: unknown }
  | { op: 'contains'; value: unknown[] };

export type Filter<T> = Partial<Record<keyof T & string, FilterOperator>>;

export interface OrderBy<T> {
  field: keyof T & string;
  direction: 'asc' | 'desc';
}

export interface QueryOptions<T> {
  filter?: Filter<T>;
  orderBy?: OrderBy<T>[];
  limit?: number;
  offset?: number;
  /** طلب عدد الصفوف الإجمالي مع النتائج (للترقيم) */
  withCount?: boolean;
}

export interface QueryResult<T> {
  rows: T[];
  total: number | null;
}

export interface Repository<T extends { id: string }> {
  list(options?: QueryOptions<T>): Promise<T[]>;
  query(options?: QueryOptions<T>): Promise<QueryResult<T>>;
  get(id: string): Promise<T | null>;
  insert(row: T): Promise<T>;
  insertMany(rows: T[]): Promise<T[]>;
  update(id: string, patch: Partial<T>): Promise<T>;
  /** تحديث مشروط — يُستخدم لمنع الكتابة المتزامنة على المهمة عبر version */
  updateIf(id: string, patch: Partial<T>, expectedVersion: number): Promise<T | null>;
  updateMany(filter: Filter<T>, patch: Partial<T>): Promise<number>;
  remove(id: string): Promise<void>;
  removeMany(filter: Filter<T>): Promise<number>;
  count(filter?: Filter<T>): Promise<number>;
}

/* =============================================================================
 * الأوامر (RPC)
 *
 * العمليات التي يجب أن تنجح كوحدة واحدة، أو التي تتطلب تحققًا في الخادم،
 * تُنفَّذ عبر أوامر مسمّاة. لا يُسمح للواجهة بتغيير حالة مهمة مباشرة.
 * ========================================================================== */

export interface CommandContext {
  requestId: string;
}

export interface CompleteTaskInput {
  taskId: UUID;
  membershipId: UUID | null;
  proofNote: string | null;
  attachmentIds: UUID[];
  actorUserId: UUID;
}

export interface ApproveTaskInput {
  taskId: UUID;
  membershipId: UUID | null;
  decision: 'approve' | 'return';
  reason: string | null;
  actorUserId: UUID;
}

export interface GenerateOccurrencesInput {
  templateId: UUID;
  throughDate: string;
  actorUserId: UUID | null;
}

export interface GenerateOccurrencesResult {
  created: number;
  skipped: number;
  lastDate: string;
}

export interface BootstrapOwnerInput {
  bootstrapToken: string;
  email: string;
  fullName: string;
  password: string;
}

export interface BootstrapStatus {
  /** هل النظام جاهز ومهيّأ (يوجد مالك منصة واحد على الأقل) */
  initialized: boolean;
  /** هل يمكن إتمام التهيئة في هذه البيئة */
  canBootstrap: boolean;
  /** هل يظهر رابط «تهيئة المنصة» في صفحة الدخول (يتحكم به مالك المنصة) */
  showBootstrapLink: boolean;
  method: 'local_token' | 'server_function' | 'unavailable';
  reason: string;
  /** نصوص صفحة الدخول العامة */
  branding: PlatformBranding;
  /** سمة المنصة الافتراضية قبل الدخول */
  theme: ThemeTokens | null;
  /** هل التسجيل الذاتي للمنشآت مفعّل؟ (إعداد عام يقرأه الزائر) */
  selfRegistrationEnabled: boolean;
  /** هل ميزة الاشتراكات التجارية مفعّلة؟ */
  subscriptionsEnabled: boolean;
}

export interface SelfRegistrationInput {
  /** مفتاح منع التكرار؛ يبقى نفسه عند إعادة المحاولة أو انقطاع الاتصال. */
  requestId: string;
  name: string;
  slug: string;
  timeZone: TimeZone;
  locale: string;
  currency: string;
}

export interface CreateCompanyInviteInput {
  companyId: UUID;
  email: string;
  roleId?: UUID | null;
  expiresHours?: number;
}

export interface CreateCompanyInviteResult {
  token: string;
  inviteId: UUID;
  expiresAt: string;
}

export interface AcceptCompanyInviteResult {
  membershipId: UUID;
  companyId: UUID;
  companyName: string;
  roleKey: string;
}

export interface SaveSubscriptionPlanInput {
  planId: UUID | null;
  code: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  billingInterval: BillingInterval;
  trialDays: number;
  features: Record<string, unknown>;
  isActive: boolean;
  sortOrder: number;
}

export interface SaveCompanySubscriptionInput {
  companyId: UUID;
  planId: UUID | null;
  status: SubscriptionStatus;
  startsAt: string | null;
  endsAt: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  source: SubscriptionSource;
  notes: string | null;
}

export interface RecordSubscriptionPaymentInput {
  companyId: UUID;
  subscriptionId: UUID | null;
  status: PaymentStatus;
  amountCents: number;
  currency: string;
  dueAt: string | null;
  paidAt: string | null;
  provider: string | null;
  providerRef: string | null;
  clientRef: string | null;
  notes: string | null;
}

export interface UpdateSubscriptionPaymentInput {
  paymentId: UUID;
  status: PaymentStatus;
  paidAt: string | null;
  notes: string | null;
}

export interface CreateCompanyInput {
  company: Omit<Company, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>;
  /** إنشاء أدوار وفئات افتراضية للشركة */
  seedDefaults: boolean;
  /** اختياري؛ لوحة المنصة تضبطه false حتى لا ينضم مالك المنصة تلقائيًا إلى العميل. */
  createOwnerMembership?: boolean;
  actorUserId: UUID;
}

export interface CancelTemplateOccurrencesInput {
  templateId: UUID;
  fromDate: string;
  actorUserId: UUID;
  actorMembershipId: UUID | null;
  reason: string;
}

export interface ApplyRetentionInput {
  companyId: UUID;
  actorUserId: UUID;
  /** dryRun يعرض العدد والمساحة المتوقعة دون حذف فعلي */
  dryRun: boolean;
  scope?: {
    brandId?: UUID | null;
    branchId?: UUID | null;
    categoryId?: UUID | null;
  };
}

export interface RetentionPreview {
  filesCount: number;
  bytesFreed: number;
  attachmentIds: UUID[];
  deletionId: UUID | null;
}

export interface PurgeCompletedTasksInput {
  companyId: UUID;
  actorUserId: UUID;
  /** عدد الأيام بعد إكمال المهمة؛ 0 يعني كل المهام المكتملة حتى اليوم */
  olderThanDays: number;
  dryRun: boolean;
}

export interface PurgeCompletedTasksResult {
  tasksCount: number;
  filesCount: number;
  bytesFreed: number;
}

export interface UploadAttachmentInput {
  companyId: UUID;
  taskId: UUID | null;
  taskAssigneeId: UUID | null;
  fileName: string;
  mimeType: string;
  kind: Attachment['kind'];
  bytes: ArrayBuffer;
  uploadedByUserId: UUID;
  clientRef: string;
}

export interface SweepNotificationsInput {
  companyId: UUID;
  actorUserId: UUID;
  withinHours: number;
}

export interface CreateTaskCommandInput {
  task: {
    brandId: UUID | null;
    branchId: UUID | null;
    categoryId: UUID;
    title: string;
    description: string | null;
    instructions: string | null;
    taskType: TaskType;
    priority: TaskPriority;
    isPersonal: boolean;
    visibility: TaskVisibility;
    assignmentMode: AssignmentMode;
    completionRule: CompletionRule;
    requiresProof: boolean;
    requiresApproval: boolean;
    approverMembershipId: UUID | null;
    assigneeMembershipIds: UUID[];
    startAt: string;
    dueAt: string | null;
    localDate: string;
    timeZone: TimeZone;
  };
  steps?: string[];
  actorMembershipId: UUID | null;
  actorUserId: UUID;
}

export interface OrgLifecycleInput {
  entityType: import('@/domain/types').OrgEntityType;
  entityId: UUID;
  action: import('@/domain/types').OrgLifecycleAction;
  /** نطاق العملية: منشأة للعلامات/الفروع/الموظفين، وفرع أو علامة لحذف ارتباط الموظف. */
  scopeType?: 'platform' | 'company' | 'brand' | 'branch';
  scopeId?: UUID | null;
  reason?: string | null;
  /** للاستعادة النهائية فقط: يكتب المستخدم اسم العنصر حرفيًا. */
  confirmation?: string | null;
}

export interface OrgArchiveQuery {
  entityType: 'company' | 'brand' | 'branch' | 'membership' | 'placement';
  scopeType: 'platform' | 'company' | 'brand' | 'branch';
  scopeId?: UUID | null;
}

export interface ArchivedOrgRecord {
  entityType: 'company' | 'brand' | 'branch' | 'membership' | 'placement';
  id: UUID;
  companyId: UUID | null;
  name: string;
  subtitle: string | null;
  status: string;
  deletedAt: string;
  deletedBy: UUID | null;
  deletedReason: string | null;
  scopeType: 'company' | 'brand' | 'branch' | null;
  scopeId: UUID | null;
  membershipId: UUID | null;
  userId: UUID | null;
  email: string | null;
  branchId: UUID | null;
  brandId: UUID | null;
}

export interface AddMembershipCommandInput {
  companyId: UUID;
  email: string;
  fullName: string | null;
  jobTitle: string | null;
  primaryBranchId: UUID | null;
  roleIds: UUID[];
  actorMembershipId: UUID | null;
  actorUserId: UUID;
  /**
   * عند تمريره: يُنشأ حساب دخول جديد بهذا البريد وكلمة المرور قبل ربط العضوية.
   * كلمة المرور تُستخدم مرة واحدة ولا تُحفظ في قاعدة التطبيق.
   * إن كان البريد مسجّلًا مسبقًا تفشل العملية بدل تغيير كلمة مروره.
   */
  password?: string | null;
}

export interface UpdateTaskCommandInput {
  taskId: UUID;
  patch: Partial<{
    brandId: UUID | null;
    branchId: UUID | null;
    categoryId: UUID;
    title: string;
    description: string | null;
    instructions: string | null;
    taskType: TaskType;
    priority: TaskPriority;
    visibility: TaskVisibility;
    completionRule: CompletionRule;
    requiresProof: boolean;
    requiresApproval: boolean;
    approverMembershipId: UUID | null;
    startAt: string;
    dueAt: string | null;
    localDate: string;
  }>;
  actorMembershipId: UUID | null;
  actorUserId: UUID;
}

export interface PlatformSupportSession {
  id: UUID;
  ownerUserId: UUID;
  companyId: UUID;
  reason: string;
  startedAt: string;
  endedAt: string | null;
}

export interface PlatformActivityEvent {
  id: string;
  companyId: UUID | null;
  companyName: string | null;
  actorUserId: UUID | null;
  actorName: string;
  actorMembershipId: UUID | null;
  action: string;
  entityType: string;
  entityId: string | null;
  severity: 'info' | 'warning' | 'critical';
  createdAt: string;
  source: 'organization' | 'support' | 'maintenance';
}

export interface PlatformActivityRetentionSettings {
  enabled: boolean;
  retentionDays: number | null;
  updatedAt: string | null;
  updatedBy: UUID | null;
}

export interface AccessSnapshot {
  companyId: UUID | null;
  membershipId: UUID | null;
  isPlatformOwner: boolean;
  isPlatformSupport: boolean;
  platformSupportSessionId: UUID | null;
  platformPermissions: PermissionKey[];
  permissions: PermissionKey[];
  companyWide: boolean;
  branchIds: UUID[];
  brandIds: UUID[];
  membershipIds: UUID[];
  supervisedMembershipIds: UUID[];
}

export interface AuditEventInput {
  companyId: UUID | null;
  action: string;
  entityType: string;
  entityId: UUID | null;
  before?: unknown;
  after?: unknown;
}

export interface SignInResult {
  userId: UUID;
  email: string;
  requiresEmailConfirmation: boolean;
}

export interface RoleDeletionImpact {
  roleId: UUID;
  roleName: string;
  assignedMembers: Array<{
    membershipId: UUID;
    fullName: string;
    membershipStatus: string;
    isDeleted: boolean;
    assignmentExpiresAt: string | null;
  }>;
  dependentRoles: Array<{ id: UUID; name: string; isDeleted: boolean }>;
}

export interface GatewayCommands {
  createTask(input: CreateTaskCommandInput): Promise<Task>;
  /** حذف نهائي للمهمة وكل بياناتها التابعة بعد إزالة ملفات التخزين فعليًا. */
  deleteTask(input: { taskId: UUID; actorUserId: UUID }): Promise<void>;
  updateTask(input: UpdateTaskCommandInput): Promise<Task>;
  addMembership(input: AddMembershipCommandInput): Promise<Membership>;
  completeTask(input: CompleteTaskInput): Promise<Task>;
  decideApproval(input: ApproveTaskInput): Promise<Task>;
  reopenTask(input: {
    taskId: UUID;
    membershipId: UUID | null;
    actorUserId: UUID;
    reason: string | null;
  }): Promise<Task>;
  cancelTask(input: {
    taskId: UUID;
    membershipId: UUID | null;
    actorUserId: UUID;
    reason: string | null;
  }): Promise<Task>;
  assignTask(input: {
    taskId: UUID;
    membershipIds: UUID[];
    actorMembershipId: UUID | null;
    actorUserId: UUID;
  }): Promise<Task>;
  generateOccurrences(input: GenerateOccurrencesInput): Promise<GenerateOccurrencesResult>;
  /** يعرض أسماء المرتبطين بالدور ومراجع التسلسل قبل طلب موافقة الحذف. */
  getRoleDeletionImpact(input: { companyId: UUID; roleId: UUID }): Promise<RoleDeletionImpact>;
  /** يحذف إسنادات الدور وينظف مراجع التسلسل ذريًا بعد مطابقة المعاينة المؤكدة. */
  deleteCompanyRole(input: {
    companyId: UUID;
    roleId: UUID;
    expectedMemberIds: UUID[];
    expectedDependentRoleIds: UUID[];
  }): Promise<void>;
  /** قائمة الأدوار المحذوفة لمالك المنشأة أو حامل صلاحية إدارة الأدوار. */
  listDeletedCompanyRoles(input: { companyId: UUID }): Promise<Role[]>;
  /** استعادة الدور من السلة دون إعادة إسناد الموظفين أو مراجع التسلسل القديمة. */
  restoreCompanyRole(input: { companyId: UUID; roleId: UUID }): Promise<Role>;
  createCompany(input: CreateCompanyInput): Promise<Company>;
  /** إجراء إداري موحّد للحذف المنطقي والاستعادة والتنشيط والإيقاف والحذف النهائي. */
  manageOrgEntity(input: OrgLifecycleInput): Promise<void>;
  /** قائمة المحذوفات ضمن نطاق مصرّح به فقط. */
  listOrgArchive(input: OrgArchiveQuery): Promise<ArchivedOrgRecord[]>;
  /** تحديث ملف العرض التنظيمي والمسمى الوظيفي لعضو، دون تعديل الهوية العالمية. */
  updateMembershipProfile(input: {
    companyId: UUID;
    membershipId: UUID;
    fullName: string;
    jobTitle: string | null;
  }): Promise<void>;
  /** تغيير خادمي لبيانات الدخول، ولا يتاح إلا عبر Edge Function المصرّح. */
  updateMemberAccountAuth?(input: {
    companyId: UUID;
    membershipId: UUID;
    email?: string;
    password?: string;
  }): Promise<{ email: string; emailChanged: boolean; passwordChanged: boolean; auditWarning?: string }>;
  /** مسح شامل لمالك المنصة مع إبقاء نتيجة صيانة مختصرة خارج سجلات الأحداث. */
  purgeAuditLogs(input: { confirmation: string }): Promise<{
    auditLogsDeleted: number;
    supportAuditDeleted: number;
    supportSessionsDeleted: number;
    activeSupportSessionsRetained: number;
  }>;
  purgeCompanyActivityLogs(input: { companyId: UUID; confirmation: string }): Promise<{
    auditLogsDeleted: number;
    supportAuditRetained: number;
    activeSupportSessionsRetained: number;
  }>;
  listPlatformActivity(input?: { limit?: number }): Promise<PlatformActivityEvent[]>;
  getPlatformActivityRetention(): Promise<PlatformActivityRetentionSettings>;
  setPlatformActivityRetention(input: {
    enabled: boolean;
    retentionDays: number | null;
  }): Promise<PlatformActivityRetentionSettings>;
  bootstrapStatus(): Promise<BootstrapStatus>;
  bootstrapPlatformOwner(input: BootstrapOwnerInput): Promise<SignInResult>;
  /** مالك المنصة وحده: إظهار/إخفاء رابط التهيئة في صفحة الدخول. */
  setBootstrapLinkVisibility(input: { visible: boolean }): Promise<void>;
  /** مالك المنصة وحده: نصوص صفحة الدخول وسمة المنصة الافتراضية. */
  setPlatformBranding(input: {
    branding: PlatformBranding;
    theme: ThemeTokens | null;
  }): Promise<void>;
  /** مالك المنصة وحده: إعدادات التسجيل والتجارة. */
  getPlatformOperationsSettings(): Promise<PlatformOperationsSettings>;
  setPlatformOperationsSettings(
    input: Omit<PlatformOperationsSettings, 'id' | 'updatedAt' | 'updatedBy'>,
  ): Promise<PlatformOperationsSettings>;
  /** تسجيل منشأة ذاتيًا بعد إنشاء/تأكيد حساب المسؤول. */
  selfRegisterCompany(input: SelfRegistrationInput): Promise<Company>;
  /** دعوة مسؤول منشأة برابط لمرة واحدة؛ يعيد الرمز مرة واحدة فقط. */
  createCompanyInvite(input: CreateCompanyInviteInput): Promise<CreateCompanyInviteResult>;
  acceptCompanyInvite(input: { token: string }): Promise<AcceptCompanyInviteResult>;
  saveSubscriptionPlan(input: SaveSubscriptionPlanInput): Promise<SubscriptionPlan>;
  saveCompanySubscription(input: SaveCompanySubscriptionInput): Promise<CompanySubscription>;
  recordSubscriptionPayment(input: RecordSubscriptionPaymentInput): Promise<SubscriptionPayment>;
  updateSubscriptionPayment(input: UpdateSubscriptionPaymentInput): Promise<SubscriptionPayment>;
  /** ملفات العرض المنفصلة لكل عضوية؛ لا تُرجع بيانات الملف العالمي. */
  listCompanyProfiles(input: { companyId: UUID }): Promise<
    Array<{ membershipId: UUID; userId: UUID; fullName: string; phone: string | null; avatarDataUrl: string | null }>
  >;
  /** عناوين البريد لأغراض إدارة العضويات فقط، وتتطلب صلاحية user.view. */
  listCompanyMemberEmails(input: { companyId: UUID }): Promise<Array<{ membershipId: UUID; email: string }>>;
  listPlatformMembers(): Promise<Array<{ userId: UUID; email: string; fullName: string; roleName: string; status: 'active' | 'suspended'; permissions: PermissionKey[] }>>;
  savePlatformMember(input: { email: string; roleName?: string; permissions: PermissionKey[] }): Promise<void>;
  createPlatformMember?(input: { fullName: string; email: string; password: string; roleName: string; permissions: PermissionKey[] }): Promise<void>;
  removePlatformMember(input: { userId: UUID }): Promise<void>;
  cancelTemplateOccurrences(input: CancelTemplateOccurrencesInput): Promise<number>;
  applyRetention(input: ApplyRetentionInput): Promise<RetentionPreview>;
  purgeCompletedTasks(input: PurgeCompletedTasksInput): Promise<PurgeCompletedTasksResult>;
  uploadAttachment(input: UploadAttachmentInput): Promise<Attachment>;
  deleteAttachment(input: { attachmentId: UUID; actorUserId: UUID; mode: 'manual' }): Promise<void>;
  /** رابط مؤقت لعرض مرفق خاص (لا يُخزَّن ولا يُشارك) */
  createAttachmentUrl(input: { attachmentId: UUID; expiresInSeconds: number }): Promise<string>;
  markNotificationRead(input: { notificationId: UUID; actorUserId: UUID }): Promise<void>;
  markAllNotificationsRead(input: { companyId: UUID; actorUserId: UUID }): Promise<number>;
  /** توليد تنبيهات الاستحقاق القريب/التأخير للمستخدم الحالي فقط (idempotent) */
  sweepMyNotifications(input: SweepNotificationsInput): Promise<number>;
}

/* =============================================================================
 * المصادقة
 * ========================================================================== */

export interface AuthSession {
  userId: UUID;
  email: string;
  expiresAt: string;
}

export interface SignUpInput {
  email: string;
  password: string;
  fullName: string;
  /** معرّف الشركة إن كان الانضمام عبر دعوة */
  inviteCompanyId?: UUID | null;
}

export interface AuthGateway {
  getSession(): Promise<AuthSession | null>;
  signIn(email: string, password: string): Promise<SignInResult>;
  signUp(input: SignUpInput): Promise<SignInResult>;
  signOut(): Promise<void>;
  /** تغيير كلمة المرور بعد التحقق من الكلمة الحالية. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  /** إرسال كود تحقق (6–10 أرقام حسب إعداد Supabase) إلى البريد لاستعادة كلمة المرور. */
  requestPasswordResetCode(email: string): Promise<void>;
  /** التحقق من الكود؛ يفتح جلسة استعادة مؤقتة. */
  verifyPasswordResetCode(email: string, code: string): Promise<void>;
  /** تعيين كلمة المرور الجديدة بعد التحقق من الكود. */
  completePasswordReset(newPassword: string): Promise<void>;
  /** طلب تغيير البريد؛ يتطلب تأكيدًا يصل إلى البريد (SMTP). */
  requestEmailChange(newEmail: string): Promise<void>;
  onAuthStateChange(callback: (session: AuthSession | null) => void): () => void;
}

/* =============================================================================
 * العقد الكامل
 * ========================================================================== */

export interface DataGateway {
  readonly kind: 'local' | 'supabase';
  readonly auth: AuthGateway;
  readonly commands: GatewayCommands;
  /** هل تعمل هذه الطبقة دون اتصال */
  readonly supportsOffline: boolean;
  /** تثبيت الشركة النشطة في السياق (الوضع المحلي). لا تُقبل كدليل صلاحية. */
  setActiveCompany?(companyId: string | null): Promise<void>;
  /**
   * إعادة تحميل/مزامنة سياق الجلسة قبل القراءة. في الوضع المحلي يُعاد بناء
   * سياق السياسة من الجلسة والعضويات؛ لا تُقبل أي قيمة من الواجهة.
   */
  refresh?(): Promise<void>;
  table<T extends TableName>(name: T): Repository<Row<T>>;
  /** تنفيذ مجموعة عمليات كوحدة واحدة */
  transaction<T>(work: () => Promise<T>): Promise<T>;
  /** مسح كل البيانات المحلية — يُستخدم عند تسجيل الخروج أو تغيير المستخدم */
  resetLocalState?(): Promise<void>;
  /** تسجيل حدث تدقيق من طبقة موثوقة (الخدمات) بعد التحقق من الصلاحية */
  recordAudit(input: AuditEventInput): Promise<void>;
  /** لقطة صلاحيات/نطاق المستخدم الحالي لعرض الواجهة (ليست مرجع الحماية) */
  getAccessSnapshot?(companyId?: string | null): Promise<AccessSnapshot>;
  /** جلسة دعم خادمية لمالك المنصة؛ غير متاحة في التخزين المحلي. */
  getPlatformSupportSession?(): Promise<PlatformSupportSession | null>;
  beginPlatformSupportAccess?(input: { companyId: UUID; reason: string }): Promise<PlatformSupportSession>;
  endPlatformSupportAccess?(sessionId: UUID): Promise<void>;
}

/** صلاحية مطلوبة لكل عملية كتابة على جدول، أو دالة مخصصة. */
export type WriteRule =
  | { kind: 'permission'; permission: PermissionKey }
  | { kind: 'permissions'; permissions: PermissionKey[] }
  | { kind: 'platformOwner' }
  | { kind: 'self' }
  | { kind: 'taskScoped' }
  | { kind: 'appendOnly' }
  | { kind: 'deny' };

export interface TablePolicy {
  /** الحقل الذي يحمل معرّف الشركة (null = جدول غير مرتبط بشركة) */
  tenantField: string | null;
  /** صلاحية القراءة، أو null للقراءة المشروطة (تُحدَّد بدالة) */
  readPermission: PermissionKey | null;
  readRule: 'tenantMember' | 'platformOwner' | 'selfRow' | 'taskScoped' | 'public';
  writeRule: WriteRule;
  softDelete: boolean;
  /** هل يحتوي الجدول على معرّف شركة يجب فرضه عند الإدراج */
  requiresTenant: boolean;
}
